import React, { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { track } from '@adthlete/shared-frontend'
import { toast } from '@/components/ui/sonner'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/hooks/useAuth'
import {
  useBookLabCallMutation,
  useCancelLabCallMutation,
  type LabCallState,
} from '@/redux/collaborationsApi'
import { cn } from '@/lib/utils'

/** Shown in the Lab's own time zone (the campus's), with "ET" after it */
const fmt = (iso: string, tz: string, opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts }).format(new Date(iso))
const dayKey = (iso: string, tz: string) =>
  fmt(iso, tz, { year: 'numeric', month: '2-digit', day: '2-digit' })
const callWhen = (iso: string, tz: string) =>
  `${fmt(iso, tz, { weekday: 'short', month: 'short', day: 'numeric' })} at ${fmt(iso, tz, { hour: 'numeric', minute: '2-digit' })} ET`

/**
 * Book the strategy call with the Creator Lab in two taps: a day, then a time. The Lab takes
 * calls around lunch and 4-5 pm, when athletes are free, and calls the athlete's phone. Once
 * booked it shows when, with Change and Cancel. Days and times wrap; nothing scrolls sideways.
 */
const StrategyCallPicker: React.FC<{ state: LabCallState }> = ({ state }) => {
  const { athlete } = useAuth()
  const [book, { isLoading: booking }] = useBookLabCallMutation()
  const [cancel, { isLoading: cancelling }] = useCancelLabCallMutation()
  const tz = state.timezone
  const booked = state.call?.status === 'booked' ? state.call : null
  const [changing, setChanging] = useState(false)
  const [phone, setPhone] = useState(booked?.phone ?? athlete?.phone ?? '')

  // The open times, by day
  const days = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const t of state.times) {
      const k = dayKey(t, tz)
      map.set(k, [...(map.get(k) ?? []), t])
    }
    return [...map.values()]
  }, [state.times, tz])
  const [day, setDay] = useState(0)
  const [time, setTime] = useState<string | null>(null)

  if (booked && !changing) {
    return (
      <div className="mt-1.5 rounded-xl bg-[#C0FD72]/15 px-3 py-2.5">
        <p className="font-semibold text-foreground">Booked: {callWhen(booked.startsAt, tz)}</p>
        <p className="text-xs text-gray-600">
          {booked.minutes} minutes. The Lab calls you{booked.phone ? ` at ${booked.phone}` : ''}.
        </p>
        <div className="mt-2 flex gap-4 text-sm font-semibold">
          <button
            type="button"
            onClick={() => {
              setChanging(true)
              setTime(null)
            }}
            className="text-[#4d7c0f]"
          >
            Change time
          </button>
          <button
            type="button"
            disabled={cancelling}
            onClick={async () => {
              try {
                await cancel().unwrap()
                track('lab_call_cancelled')
                toast('Call cancelled', { description: 'Pick a new time whenever suits you.' })
              } catch {
                toast.error("Couldn't cancel. Try again.")
              }
            }}
            className="text-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (!days.length) {
    return <p className="mt-1.5">We'll text you to set a time.</p>
  }

  const submit = async () => {
    if (!time) return
    try {
      await book({ startsAt: time, phone: phone.trim() || undefined }).unwrap()
      track(changing ? 'lab_call_moved' : 'lab_call_booked', { hour: fmt(time, tz, { hour: 'numeric' }) })
      toast('Call booked', { description: `${callWhen(time, tz)}. The Lab will call you.` })
      setChanging(false)
    } catch (err: any) {
      toast.error(err?.data?.error ?? "Couldn't book that time. Pick another.")
    }
  }

  const today = days[Math.min(day, days.length - 1)]
  return (
    <div className="mt-2 space-y-3">
      <p className="text-xs text-gray-500">Pick a day, then a time (Eastern).</p>
      <div className="grid grid-cols-4 gap-1.5" role="group" aria-label="Day">
        {days.map((times, i) => (
          <button
            key={times[0]}
            type="button"
            onClick={() => {
              setDay(i)
              setTime(null)
            }}
            aria-pressed={i === day}
            className={cn(
              'rounded-lg border px-1 py-1.5 text-center text-xs leading-tight',
              i === day
                ? 'border-[#84CC16] bg-[#C0FD72]/30 font-bold text-foreground'
                : 'border-border bg-card text-gray-700',
            )}
          >
            <span className="block">{fmt(times[0], tz, { weekday: 'short' })}</span>
            <span className="block font-semibold">
              {fmt(times[0], tz, { month: 'short', day: 'numeric' })}
            </span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5" role="group" aria-label="Time">
        {today.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTime(t)}
            aria-pressed={t === time}
            className={cn(
              'h-10 rounded-lg border text-sm',
              t === time
                ? 'border-[#84CC16] bg-[#C0FD72] font-bold text-[#0F172A]'
                : 'border-border bg-card text-foreground',
            )}
          >
            {fmt(t, tz, { hour: 'numeric', minute: '2-digit' })}
          </button>
        ))}
      </div>
      <label className="block">
        <span className="mb-1 block text-xs text-gray-600">The number we call</span>
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-10 text-base"
        />
      </label>
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!time || booking}
          onClick={submit}
          className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#C0FD72] text-sm font-bold text-[#0F172A] disabled:opacity-50"
        >
          {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {time ? `Book ${callWhen(time, tz)}` : 'Pick a time'}
        </button>
        {changing ? (
          <button
            type="button"
            onClick={() => setChanging(false)}
            className="text-sm font-semibold text-gray-600"
          >
            Keep my time
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default StrategyCallPicker
